<script setup lang="ts">
import { RouterView, useRouter } from 'vue-router'
import { ref, computed } from 'vue'
import { useAppStore } from '@/stores/appStore'
import { useAuthStore } from '@/stores/authStore'
import { updates } from '@/data/updates'

const drawer = ref(false)
const miniState = ref(true)
const appStore = useAppStore()
const authStore = useAuthStore()
const router = useRouter()
const latestVersion = computed(() => {
  return updates[0].version
})

const getBannerInfo = computed(() => {
  // Check subscription status first
  if (appStore.subscription === null || !['trialing', 'active'].includes(appStore.subscription?.status)) {
    return {
      message: 'Your subscription is not active! Consider checking your billing information!',
      style: 'negative'
    }
  }

  // Check trial status
  if (appStore.subscription.status === 'trialing' && !appStore.subscription.setupPayment) {
    const trialEnd = appStore.subscription.trialEnd ? new Date(appStore.subscription.trialEnd) : new Date()
    const now = new Date()
    const diffTime = trialEnd.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays <= 0) {
      return {
        message: 'Your trial has ended. Consider upgrading to a paid plan!',
        style: 'negative'
      }
    }
    if (diffDays <= 4) {
      return {
        message: `Your trial is ending soon. Consider upgrading to a paid plan! ${diffDays} days left.`,
        style: 'warning'
      }
    }
  }

  // Check usage quotas
  if (appStore.usage.outOf > 0) {
    const individualPercentage = appStore.usage.individual / appStore.usage.outOf
    const teamPercentage = appStore.usage.team / appStore.usage.outOf

    if (individualPercentage >= 1 || teamPercentage >= 1) {
      return {
        message:
          'You have reached 100% of your usage quota. Please upgrade your plan to add more quota and continue using all features!',
        style: 'negative'
      }
    }
    if (individualPercentage > 0.9 || teamPercentage > 0.9) {
      return {
        message:
          'You are using more than 90% of your usage quota. Please consider upgrading your plan to add more quota!',
        style: 'negative'
      }
    }
    if (individualPercentage > 0.8 || teamPercentage > 0.8) {
      return {
        message: 'You are using more than 80% of your usage quota. We recommend upgrading your plan to add more quota.',
        style: 'warning'
      }
    }
  }

  return {
    message: '',
    style: 'warning'
  }
})

const logout = async () => {
  await authStore.logout()
  router.go(0)
}
const menuItems = [
  { title: 'Home', icon: 'mdi-home', route: '/' },
  { title: 'Profiles', icon: 'mdi-account-group-outline', route: '/profiles' },
  { title: 'Billing', icon: 'mdi-invoice-text-outline', route: '/billing' },
  { title: 'Referrals', icon: 'mdi-share-variant', route: '/referrals' },
  { title: 'Support', icon: 'mdi-headset', route: '/support' },
  { title: 'Settings', icon: 'mdi-cog', route: '/settings' },
  { title: 'Admin', icon: 'mdi-shield-account', route: '/admin', adminOnly: true },
  { title: 'Affiliate Dashboard', icon: 'mdi-chart-line', route: '/affiliate', adminOnly: true }
]

const filteredMenuItems = computed(() => {
  return menuItems.filter((item) => {
    if (item.adminOnly) {
      const userID = authStore.user?.userID
      return userID === 1 || userID === 2
    }
    return true
  })
})
</script>

<template>
  <q-layout view="hHh Lpr lff" style="width: 100%">
    <q-drawer
      v-model="drawer"
      show-if-above
      :width="220"
      :mini-width="70"
      :breakpoint="250"
      :mini="miniState"
      @mouseenter="miniState = false"
      @mouseleave="miniState = true"
      mini-to-overlay
      bordered
      class="bg-black"
    >
      <nav class="row">
        <q-list class="col-12 menu-list">
          <q-item>
            <q-item-section avatar>
              <q-img src="/favicon-32x32.png" style="width: 32px; height: 32px" />
            </q-item-section>
            <q-item-section class="text-white text-h6">MeetingHero</q-item-section>
          </q-item>
          <q-separator />
          <q-item
            v-for="(item, index) in filteredMenuItems"
            :key="index"
            clickable
            :active="$route.path === item.route"
            active-class="bg-primary text-white"
            v-ripple
            :to="item.route"
          >
            <q-item-section avatar>
              <q-icon :name="item.icon" size="lg" />
            </q-item-section>
            <q-item-section class="text-h6">{{ item.title }}</q-item-section>
          </q-item>
        </q-list>
      </nav>
      <div class="version-display text-grey-6 text-caption q-px-md q-py-sm">
        <router-link to="/updates" class="text-grey-6 text-decoration-none">v{{ latestVersion }}</router-link>
      </div>
    </q-drawer>

    <q-page-container>
      <q-banner
        v-if="getBannerInfo.message !== '' && !$route.path.includes('onboarding')"
        inline-actions
        :class="'bg-' + getBannerInfo.style"
        class="text-white"
      >
        {{ getBannerInfo.message }}
        <template v-slot:action>
          <q-btn flat color="white" label="Billing" to="/billing" />
        </template>
      </q-banner>
      <q-page padding style="padding-top: 0; min-height: calc(100vh - 100px)">
        <RouterView />
      </q-page>
    </q-page-container>

    <q-dialog v-model="appStore.showDialog" position="bottom">
      <q-card style="width: 350px">
        <q-linear-progress v-if="appStore.errorType" :value="1" color="red" />

        <q-card-section class="row items-center no-wrap">
          <div v-if="appStore.errorType === 'Generic'">
            <div class="text-bold">{{ appStore.errorMessage }}</div>
          </div>
          <div v-if="appStore.errorType === 'Auth'">
            <div class="text-bold">{{ appStore.errorMessage }}</div>
            <q-btn color="accent" label="Logout" @click="logout" />
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-layout>
</template>

<style scoped>
.menu-list > .q-item {
  border-radius: 8px;
  margin: 10px 10px 0px 10px;
  color: #9ca3af;
}

.menu-list > .q-item:first-child {
  margin-bottom: 10px;
}

.version-display {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
